<script>
  import { createEventDispatcher } from 'svelte';
  import Button from '@components/Button.svelte';
  import CreatorSpotlightAjax from '@ajax/creator_spotlight';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let maxSelected = 4;
  let availableInterests = [];
  let selectedInterests = [];
  let warn = false;

  let loadAllCreatorSpotlightInterests = async function () {
    let res = await CreatorSpotlightAjax.getAllSpotlightInterests();
    availableInterests = res.interests;
  };

  let loadUserCreatorSpotlightInterests = async function () {
    let res = await CreatorSpotlightAjax.getSpotlightInterests();
    selectedInterests = [...res.interests];
  };

  let setCreatorSpotlightInterest = async function (val) {
    let res = await CreatorSpotlightAjax.createSpotlightInterest({ id: val });
    return res;
  };

  let removeCreatorSpotlightInterest = async function (val) {
    let res = await CreatorSpotlightAjax.deleteSpotlightInterest(val);
    warn = false;
    return res;
  };

  const toggleInterestsSelect = function (currentValue) {
    if (selectedInterests.some((i) => i.id === currentValue.id)) {
      selectedInterests = selectedInterests.filter(function (interest) {
        return interest.id != currentValue.id;
      });
      removeCreatorSpotlightInterest(currentValue.id);
    } else {
      if (selectedInterests.length < maxSelected) {
        selectedInterests = [...selectedInterests, currentValue];
        setCreatorSpotlightInterest(currentValue.id);
      } else {
        warn = true;
      }
    }
  };

  $: isSelected = (id) => {
    return selectedInterests.some((i) => i.id === id);
  };
  $: selectedSpotsRemaining = maxSelected - selectedInterests.length;

  loadUserCreatorSpotlightInterests();
  loadAllCreatorSpotlightInterests();
</script>

<style style="text/scss" lang="scss">
  .creator-spotlight-interests {
    .ui-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      gap: 16px;
    }
    .ui-title {
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      max-width: 350px;
    }
    .ui-subtitle {
      margin-bottom: 16px;
      color: #717171;

      &.warn {
        color: #ff5567;
      }
    }
    .ui-back,
    .ui-close {
      background-color: transparent;
      border: none;
      margin: 0;
      padding: 0;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    }

    .select-container {
      display: inline-flex;
      gap: 12px;
      flex-wrap: wrap;

      &.justify-center {
        justify-content: center;
      }
    }

    .ui-select-label {
      border: 1px solid #e3e3e3;
      color: #717171;
      padding: 8px 16px;
      border-radius: 32px;
      font-weight: 500;
      cursor: pointer;
      transition: 200ms;

      @media (hover: hover) {
        &:hover {
          background-color: #f5f5f5;
        }
      }

      &.selected {
        border-color: #717171;
        background-color: #f1f1f1;
        color: #1c1c1c;
      }

      &.disabled {
        cursor: default;
        background: none;
      }
    }

    .ui-select-label__empty {
      border: 1px dashed #c6c6c6;
      height: 40.5px;
      width: 100px;
      padding: 0;
      cursor: default;
      color: rgba(0, 0, 0, 0);
    }

    .selected-section,
    .suggested-section {
      text-align: left;
      margin-bottom: 32px;

      .ui-subtitle {
        margin-bottom: 8px;
      }
    }

    .suggested-section {
      margin-bottom: 24px;

      .select-container {
        max-height: 47dvh;
        overflow-y: auto;
        /* Hide scrollbar for IE, Edge and Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
      }

      /* Hide scrollbar for Chrome, Safari and Opera */
      .select-container::-webkit-scrollbar {
        display: none;
      }
    }
  }
</style>

<div class="creator-spotlight-interests" class:mobile={mobile}>
  <div class="ui-header">
    <button
      class="ui-back"
      on:click={() => {
        dispatch('back');
      }}>
      <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4.85501 7.26609L10.5901 1.49831L11.6538 2.55596L5.91867 8.32373C5.82171 8.42125 5.82171 8.57877 5.91867 8.67628L11.6538 14.4441L10.5901 15.5017L4.85501 9.73393C4.17626 9.05132 4.17626 7.94869 4.85501 7.26609Z"
          fill="#1C1C1C" />
      </svg>
    </button>
    <div class="ui-title">Some of my interests</div>
    <button
      class="ui-close"
      on:click={() => {
        dispatch('close');
      }}
      ><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.93932 8.50003L2.00049 13.4386L3.06112 14.4993L8 9.56066L12.9389 14.4993L13.9995 13.4386L9.06069 8.50003L13.9995 3.56142L12.9389 2.50073L8 7.43939L3.06112 2.50073L2.00049 3.56142L6.93932 8.50003Z"
          fill="#1C1C1C" />
      </svg>
    </button>
  </div>
  <p class="ui-subtitle" class:warn={warn}>Select up to {maxSelected}</p>

  <div class="selected-section">
    <div class="ui-subtitle">My interests</div>
    <div class="select-container">
      {#each selectedInterests as interest}
        <div class="select-item">
          <div class="ui-select-label" class:selected={true} on:click={() => toggleInterestsSelect(interest)}>
            {interest.value}
          </div>
        </div>
      {/each}
      {#each Array(selectedSpotsRemaining) as _}
        <div class="select-item">
          <div class="ui-select-label ui-select-label__empty">_</div>
        </div>
      {/each}
    </div>
  </div>
  <div class="suggested-section">
    <div class="ui-subtitle">Suggested</div>
    <div class="select-container justify-center">
      {#each availableInterests as interest}
        <div class="select-item">
          <div
            class="ui-select-label"
            class:selected={isSelected(interest.id)}
            class:disabled={selectedInterests.length === maxSelected && !isSelected(interest.id)}
            on:click={() => toggleInterestsSelect(interest)}>
            {interest.value}
          </div>
        </div>
      {/each}
    </div>
  </div>
  <div class="finished-btn">
    <Button
      disabled={selectedInterests.length < 2}
      colorScheme="black"
      style="padding-left: 40px; padding-right: 40px; width: auto; margin: 0;"
      text={selectedInterests.length < 2 ? 'Select at least 2' : 'Continue'}
      on:click={() => {
        dispatch('finished');
      }} />
  </div>
</div>
